import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const DARK_PRESET = {
  productStyleMode: 'dark',
  bgColor: '#0f172a',
  surfaceColor: '#1e293b',
  surfaceAltColor: '#0f172a',
  heroBg: '#1e293b',
  textColor: '#f8fafc',
  headingColor: '#ffffff',
  mutedColor: '#94a3b8',
  primaryColor: '#3b82f6',
  borderColor: '#334155',
  btnBg: '#3b82f6',
  btnText: '#ffffff',
};

const LIGHT_PRESET = {
  productStyleMode: 'light',
  bgColor: '#f7f7f7',
  surfaceColor: '#ffffff',
  surfaceAltColor: '#f8fafc',
  heroBg: '#ffffff',
  textColor: '#0f172a',
  headingColor: '#0f172a',
  mutedColor: '#64748b',
  primaryColor: '#2563eb',
  borderColor: '#e5e7eb',
  btnBg: '#2563eb',
  btnText: '#ffffff',
};

// cardMinWidth values that approximate columns in a typical 800px container
const PER_ROW_WIDTH = { 1: 1400, 2: 380, 3: 240, 4: 160 };

function detectPerRow(minWidth) {
  const w = Number(minWidth) || 220;
  if (w >= 400) return 1;
  if (w >= 280) return 2;
  if (w >= 190) return 3;
  return 4;
}

const StyleSettingsPage = ({ restaurantId }) => {
  const [styleData, setStyleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('restaurants')
        .select('style_data')
        .eq('id', restaurantId)
        .maybeSingle();
      if (fetchError) throw fetchError;
      setStyleData(data?.style_data || {});
    } catch (err) {
      setError(err.message || 'Failed to load style settings.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const save = async (updates) => {
    const next = { ...styleData, ...updates };
    setStyleData(next);
    setSaving(true);
    setError('');
    try {
      const { error: patchError } = await supabase
        .from('restaurants')
        .update({ style_data: next })
        .eq('id', restaurantId);
      if (patchError) throw patchError;
      try { localStorage.setItem('ss_style_v1', JSON.stringify(next)); } catch {}
      setNotice('Saved.');
      setTimeout(() => setNotice(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to save.');
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ssp-state">Loading style settings...</div>;

  const isDark = styleData?.productStyleMode === 'dark';
  const perRow = detectPerRow(styleData?.cardMinWidth);
  const layout = styleData?.layoutMode || 'grid';

  return (
    <div className="ssp-page">
      <div className="ssp-header">
        <h1>Style</h1>
        <p>Control how products appear on your ordering page.</p>
      </div>

      {error && <div className="ssp-notice ssp-notice-error">{error}</div>}
      {notice && <div className="ssp-notice ssp-notice-success">{notice}</div>}

      <div className="ssp-sections">
        <section className="ssp-card">
          <h2>Colour Theme</h2>
          <p>Choose the background style for your products page.</p>
          <div className="ssp-mode-grid">
            <button
              className={`ssp-mode-btn${!isDark ? ' active' : ''}`}
              onClick={() => save(LIGHT_PRESET)}
              disabled={saving}
            >
              <div className="ssp-mode-preview ssp-preview-light">
                <div className="ssp-preview-card" />
                <div className="ssp-preview-card" />
              </div>
              <span>Light</span>
            </button>
            <button
              className={`ssp-mode-btn${isDark ? ' active' : ''}`}
              onClick={() => save(DARK_PRESET)}
              disabled={saving}
            >
              <div className="ssp-mode-preview ssp-preview-dark">
                <div className="ssp-preview-card dark" />
                <div className="ssp-preview-card dark" />
              </div>
              <span>Dark</span>
            </button>
          </div>
        </section>

        <section className="ssp-card">
          <h2>Products Per Row</h2>
          <p>How many product cards appear side by side.</p>
          <div className="ssp-seg">
            {[1, 2, 3, 4].map(n => (
              <button
                key={n}
                className={`ssp-seg-btn${perRow === n ? ' active' : ''}`}
                onClick={() => save({ cardMinWidth: PER_ROW_WIDTH[n] })}
                disabled={saving}
              >
                {n}
              </button>
            ))}
          </div>
        </section>

        <section className="ssp-card">
          <h2>Layout</h2>
          <p>How products are arranged on the page.</p>
          <div className="ssp-seg">
            <button
              className={`ssp-seg-btn${layout === 'grid' ? ' active' : ''}`}
              onClick={() => save({ layoutMode: 'grid' })}
              disabled={saving}
            >
              Grid
            </button>
            <button
              className={`ssp-seg-btn${layout === 'list' ? ' active' : ''}`}
              onClick={() => save({ layoutMode: 'list' })}
              disabled={saving}
            >
              List
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StyleSettingsPage;
